/* eslint-disable no-nested-ternary */
import React, { useCallback, useLayoutEffect, useMemo, memo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { AppView, CurrencyAmountText, Spacer } from 'design-system';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconStatusDone from 'design-system/icons/status/IconStatusDone';
import IconStatusPending from 'design-system/icons/status/IconStatusPending';
import IconTooltip from 'design-system/icons/IconTooltip';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import Squircle from 'design-system/Squircle';
import Text from 'design-system/Text';
import { hitSlopS, rem } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import ProgressCard from 'features/invest/components/InvestOnboardingFunnel/ProgressCard';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, formatDateForItem, formatPlural } from 'utils/formatting';
import AutoInvestFeeInfoModal from '../components/AutoInvestFeeInfoModal';
import { isWeb } from '../../../constants';
const AutoinvestActivityTimelineScreen = ({ route, navigation }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const { activity } = route.params;
    const stockMetaCache = useAppSelector((state) => state.invest.caches);
    const goBack = useCallback(() => {
        navigation.pop();
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => <NavigationCloseButton inWebHeader onPress={goBack}/>,
        });
    }, [goBack]);
    const isCompleted = activity.status === 'completed' || activity.status === 'partially_completed';
    const [allocations, rejectedAllocations, rejectedAllocationsLength] = useMemo(() => {
        if (activity.status === 'partially_completed') {
            const allocationsArr = [];
            const rejectedAllocationsArr = [];
            activity.allocations.forEach((allocation) => {
                if (allocation.status === 'rejected') {
                    rejectedAllocationsArr.push(allocation);
                }
                else {
                    allocationsArr.push(allocation);
                }
            });
            return [allocationsArr, rejectedAllocationsArr, rejectedAllocationsArr.length];
        }
        return [activity.allocations, undefined, 0];
    }, [activity.allocations, activity.status]);
    const statusName = useMemo(() => {
        switch (activity.status) {
            case 'pending':
                return ' pending';
            case 'completed':
                return ' completed';
            case 'partially_completed':
                return 'Partially completed';
            default:
                return '';
        }
    }, [activity.status]);
    const paymentCollected = !!activity.paymentCollectedAt;
    const ordersPlaced = !!activity.ordersPlacedAt;
    const ordersSettled = !!activity.ordersSettledAt;
    const feeAmount = activity.fee?.value;
    const feeCurrency = activity.fee?.currency ?? activity.amount.currency;
    const feeFormatted = formatAmount(feeAmount, feeCurrency, 2);
    const showFeeModal = useCallback(() => {
        if (feeAmount !== undefined) {
            AutoInvestFeeInfoModal.show({ value: feeAmount, currency: feeCurrency });
        }
    }, [feeAmount, feeCurrency]);
    const step1TimeInfo = activity.initiatedAt
        ? formatDateForItem(new Date(activity.initiatedAt))
        : activity.initiationEta
            ? formatDateForItem(new Date(activity.initiationEta), 'Scheduled')
            : 'Pending';
    const step2TimeInfo = activity.paymentCollectedAt
        ? formatDateForItem(new Date(activity.paymentCollectedAt))
        : activity.paymentCollectionEta
            ? formatDateForItem(new Date(activity.paymentCollectionEta))
            : 'Pending';
    const step3TimeInfo = activity.ordersSettledAt
        ? formatDateForItem(new Date(activity.ordersSettledAt))
        : activity.ordersPlacedAt
            ? formatDateForItem(new Date(activity.ordersPlacedAt))
            : activity.investmentEta
                ? formatDateForItem(new Date(activity.investmentEta))
                : '';
    const step2Subtitle = ordersPlaced
        ? 'The Autoinvest amount has been collected from your bank account.'
        : paymentCollected
            ? "We're collecting the payment. This usually takes 3 working days."
            : null;
    return (<AppView avoidHeader withFrame={false}>
      <ScrollView contentContainerStyle={contentContainerStyle}>
        <View style={styles.iconWrapper}>
          <View>
            <View style={styles.iconContainer}>
              <View style={styles.iconContainerInner}>
                <IconRocket color={colors.text.investment}/>
              </View>
            </View>
            <View style={styles.floatingStatusIconContainer}>
              <Squircle height={rem(24)} width={rem(24)} color={colors.background.dark}>
                {isCompleted ? (<IconStatusDone colorKey="positive"/>) : (<IconStatusPending color={colors.cards.onDark} bgColor={colors.elements.statusInformational}/>)}
              </Squircle>
            </View>
          </View>
        </View>
        <View style={styles.infoContainer}>
          <Text Text-14 Secondary centered>
            {activity.status === 'partially_completed' ? statusName : `Autoinvest${statusName}`}
          </Text>
          <Spacer h={8}/>
          <CurrencyAmountText size="XL" value={{
            amount: activity.amount.value,
            currency: activity.amount.currency,
        }}/>
        </View>
        <View>
          {/* Step 1: Deposit initiated */}
          <ProgressCard lightBg noTextDecoration alwaysShowTimeline alwaysShowChildren completed={paymentCollected} isActiveStep={!paymentCollected} title={paymentCollected ? 'Deposit initiated' : 'Collect payment'} timeInfo={step1TimeInfo}>
            {paymentCollected ? (<>
                <Text TextThin-14 Secondary>
                  {"We've sent the payment request to your bank."}
                </Text>
                <View style={styles.feeRow}>
                  <Text TextThin-14 Secondary>
                    Amount
                  </Text>
                  <Text NumbersThin-14 Secondary>
                    {formatAmount(activity.amount.value, activity.amount.currency, 2)}
                  </Text>
                </View>
                {feeAmount !== undefined && (<View style={styles.feeRow}>
                    <View style={styles.feeLabelRow}>
                      <Text TextThin-14 Secondary>
                        Autoinvest fee
                      </Text>
                      <Spacer w={4}/>
                      <TouchableOpacity hitSlop={hitSlopS} onPress={showFeeModal}>
                        <IconTooltip height={14} width={14} colorKey="secondary"/>
                      </TouchableOpacity>
                    </View>
                    <Text NumbersThin-14 Secondary>
                      {feeFormatted}
                    </Text>
                  </View>)}
              </>) : (<Text TextThin-14 Secondary>
                The autoinvest amount will be collected via direct debit.
              </Text>)}
          </ProgressCard>

          {/* Step 2: Collected from bank */}
          <ProgressCard lightBg noTextDecoration alwaysShowTimeline alwaysShowChildren completed={ordersPlaced} isActiveStep={paymentCollected ? !ordersPlaced : undefined} title={ordersPlaced ? 'Collected from bank' : 'Collecting from bank'} timeInfo={step2TimeInfo}>
            {step2Subtitle ? (<Text TextThin-14 Secondary>
                {step2Subtitle}
              </Text>) : null}
          </ProgressCard>

          {/* Step 3: Investing your money */}
          <ProgressCard lightBg lastItem noTextDecoration alwaysShowTimeline alwaysShowChildren completed={ordersSettled} isActiveStep={ordersPlaced ? !ordersSettled : undefined} title={ordersSettled ? 'Successfully invested' : 'Investing your money'} timeInfo={step3TimeInfo}>
            <Text TextThin-14 Secondary>
              {ordersSettled
            ? 'Your autoinvest has been completed.'
            : "We'll invest the money into your selected Funds and ETFs."}
            </Text>
            {allocations.map((alloc) => (<AllocationRow key={alloc.assetId} name={stockMetaCache?.[alloc.assetId]?.name ?? alloc.assetName ?? ''} value={alloc.amount ? formatAmount(alloc.amount.value, alloc.amount.currency, 2) : `${alloc.percentage}%`}/>))}
            {!!rejectedAllocationsLength && (<>
                <Spacer h={16}/>
                <View style={styles.separator}/>
                <Spacer h={16}/>
                <Text TextThin-14 Negative>
                  {formatPlural('This', 'These', rejectedAllocationsLength)}{' '}
                  {formatPlural('investment', 'investments', rejectedAllocationsLength)}{' '}
                  {formatPlural('wasn’t', 'weren’t', rejectedAllocationsLength)} placed because the amount invested was
                  below the fund’s minimum.
                </Text>
                {rejectedAllocations?.map((alloc) => (<AllocationRow rejected key={alloc.assetId} name={stockMetaCache?.[alloc.assetId]?.name ?? alloc.assetName ?? ''} value={alloc.amount ? formatAmount(alloc.amount.value, alloc.amount.currency, 2) : `${alloc.percentage}%`}/>))}
              </>)}
          </ProgressCard>
        </View>
      </ScrollView>
      <Button floating brand title="Close" onPress={goBack}/>
    </AppView>);
};
const AllocationRow = memo(({ name, value, rejected }) => {
    const styles = useStyles(styleSet);
    return (<View style={styles.allocationRow}>
      <View style={styles.allocationName}>
        <Text TextThin-14 Secondary numberOfLines={1}>
          {name}
        </Text>
      </View>
      <Text NumbersThin-14 Secondary style={rejected ? styles.strikeThrough : null}>
        {value}
      </Text>
    </View>);
});
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    iconWrapper: {
        alignItems: 'center',
    },
    iconContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    iconContainerInner: {
        width: rem(52),
        height: rem(52),
        borderRadius: rem(52),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.dark,
    },
    floatingStatusIconContainer: {
        right: 0,
        bottom: 0,
        width: rem(24),
        height: rem(24),
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
    },
    infoContainer: {
        paddingVertical: rem(8),
        alignItems: 'center',
    },
    feeRow: {
        paddingTop: rem(8),
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    allocationRow: {
        paddingTop: rem(16),
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    allocationName: {
        flex: 1,
        marginRight: rem(16),
    },
    feeLabelRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    strikeThrough: {
        textDecorationLine: 'line-through',
    },
    separator: {
        height: rem(1),
        marginHorizontal: -rem(16),
        backgroundColor: colors.borders.divider,
    },
}));
export default AutoinvestActivityTimelineScreen;
